import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Linkedin, Target, Gauge, Languages, Plug } from "lucide-react";
import profileImage from "../components/img/profile.jpeg";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";

const icons = [Target, Gauge, Languages, Plug];

const AboutPage = () => {
  const { t } = useI18n();
  const items = t("about.why.items");

  return (
    <div data-testid="page-about">
      <section className="relative overflow-hidden pt-8 pb-10 md:pt-14 md:pb-16">
        <div className="absolute inset-0 grid-mesh opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 lg:px-12 grid gap-10 lg:grid-cols-12 items-end">
          <div className="lg:col-span-8 reveal">
            <div className="font-mono-accent text-[11px] text-muted-foreground">{t("about.overline")}</div>
            <h1 className="mt-4 font-display text-5xl md:text-7xl font-black tracking-tighter leading-[0.92]">
              {t("about.title")}
            </h1>
          </div>
          <div className="lg:col-span-4 reveal" style={{ animationDelay: "120ms" }}>
            <p className="text-muted-foreground md:text-lg leading-relaxed">
              {t("about.sub")}
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="relative overflow-hidden rounded-sm">
            <img
              src="https://images.unsplash.com/photo-1614508569207-3295ac89d75f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxzZXJ2ZXIlMjByb29tJTIwZGF0YWNlbnRlcnxlbnwwfHx8fDE3Nzc1NTA0MTV8MA&ixlib=rb-4.1.0&q=85"
              alt="Server room"
              className="h-[52vh] w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/90 via-brand-navy/40 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-8 md:p-12">
              <p className="mt-3 max-w-3xl font-display text-2xl md:text-4xl font-bold text-white leading-tight">
                {t("about.mission.body")}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="about-why-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
          <div className="max-w-2xl">
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold tracking-tight">
              {t("about.why.title")}
            </h2>
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {items.map((it, i) => {
              const Icon = icons[i % icons.length];
              return (
                <div
                  key={i}
                  data-testid={`why-card-${i}`}
                  className="group relative overflow-hidden rounded-sm glass-card p-6 md:p-7 transition hover:-translate-y-1"
                >
                  <div className="absolute inset-0 dot-mesh opacity-60 pointer-events-none" />
                  <div className="relative">
                    <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-brand-navy text-brand-cyan ring-1 ring-brand-cyan/30">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 font-display text-lg md:text-xl font-semibold">{it.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{it.d}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32" data-testid="about-team-section">
        <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 grid gap-10 lg:grid-cols-12 items-center">
          <div className="lg:col-span-6">
            <div className="relative overflow-hidden rounded-sm">
              <img
                src={profileImage}
                alt="Todor Slavov"
                className="h-[550px] w-full object-cover"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-brand-cyan/30 rounded-sm" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <h2 className="mt-3 font-display text-3xl md:text-5xl font-bold tracking-tight">
              {t("about.team.title")}
            </h2>
            <p className="mt-4 text-muted-foreground md:text-lg leading-relaxed">
              {t("about.team.sub")}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {["Platform", "Networking", "SaaS Ops", "Application Support", "CX", "IT Helpdesk"].map((tag) => (
                <span
                  key={tag}
                  className="rounded-sm border border-border bg-background/40 px-3 py-1.5 font-mono-accent text-[10px] text-muted-foreground"
                >
                  {tag}
                </span>
              ))}
            </div>
            <div className="mt-10">
              <Button asChild className="rounded-sm font-mono-accent text-xs bg-brand-navy text-white hover:bg-brand-cyan hover:text-brand-navy dark:bg-brand-cyan dark:text-brand-navy dark:hover:bg-white btn-press">
                <a
                  href="https://www.linkedin.com/in/tslavov/"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="about-cta-btn"
                >
                  Let's connect <Linkedin className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
